import type {
  AgeRange,
  DayTicketRate,
  TicketRate,
  ActivityAvailabilityResponse,
  GraphQL,
  AvailableTimeslotsPrices,
  TimeSlot,
} from '@libs/trip-planner-interfaces';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toMoney } from './moneyResponseQL';

export function toActivityAvailabilityResponse(
  ticketResponseQL: GraphQL.searchTicketAvailabilityQuery
): ActivityAvailabilityResponse {
  assertSmartSearchList(ticketResponseQL.ratesByDay);

  return ticketResponseQL.ratesByDay.map<DayTicketRate>((rateByDay) => {
    assertSmartSearchValue(rateByDay.date);

    assertSmartSearchList(rateByDay.rates);

    const ticketRates = rateByDay.rates.map(toTicketRate);

    return { date: rateByDay.date, rates: ticketRates };
  });
}

type RatesByDayQL = NonNullable<
  GraphQL.searchTicketAvailabilityQuery['ratesByDay']
>;
type RateByDay = NonNullable<RatesByDayQL[number]>;
type TicketRatesQL = NonNullable<RateByDay['rates']>;
type TicketRateQL = NonNullable<TicketRatesQL[number]>;
function toTicketRate(ticketRateQL: TicketRateQL): TicketRate {
  assertSmartSearchList(ticketRateQL.ageRanges);

  const ageRanges = ticketRateQL.ageRanges.map(toAgeRange);

  const requiredAgeRanges =
    ticketRateQL.requiredAgeRanges?.map(toRequiredAgeRange);

  const timeSlots = ticketRateQL.timeSlots?.map(toTimeSlot);

  return {
    ...ticketRateQL,
    ageRanges,
    requiredAgeRanges,
    timeSlots,
    minimumAmountTickets: ticketRateQL.minimumAmountTickets ?? undefined,
    maximumAmountTickets: ticketRateQL.maximumAmountTickets ?? undefined,
  };
}

type AgeRangesQL = NonNullable<TicketRateQL['ageRanges']>;
type AgeRangeQL = NonNullable<AgeRangesQL[number]>;
function toAgeRange(ageRangeQL: AgeRangeQL): AgeRange {
  assertSmartSearchValue(ageRangeQL.price);
  const price = toMoney(ageRangeQL.price);

  const availableTimeslotsPrices =
    ageRangeQL.availableTimeslotsPrices?.map<AvailableTimeslotsPrices>(
      toAvailableTimeslotsPrice
    );

  return {
    ...ageRangeQL,
    maxAge: ageRangeQL.maxAge ?? null,
    price,
    availableTimeslotsPrices,
    minimumTicketsPerAgeRange:
      ageRangeQL.minimumTicketsPerAgeRange ?? undefined,
    quantityAvailable: ageRangeQL.quantityAvailable ?? undefined,
    supplierToken: ageRangeQL.supplierToken ?? undefined,
  };
}

type AvailableTimelotsPricesQL = NonNullable<
  AgeRangeQL['availableTimeslotsPrices']
>;
type AvailableTimeSlotPriceQL = AvailableTimelotsPricesQL[number];
function toAvailableTimeslotsPrice(
  availableTimeSlotPriceQL: AvailableTimeSlotPriceQL
): AvailableTimeslotsPrices {
  assertSmartSearchValue(availableTimeSlotPriceQL);

  return {
    ...availableTimeSlotPriceQL,
    quantityAvailable: availableTimeSlotPriceQL.quantityAvailable ?? undefined,
  };
}

type RequiredAgeRangesQL = NonNullable<TicketRateQL['requiredAgeRanges']>;
type RequiredAgeRangeQL = RequiredAgeRangesQL[number];
function toRequiredAgeRange(requiredAgeRangeQL: RequiredAgeRangeQL): string {
  assertSmartSearchValue(requiredAgeRangeQL);

  return requiredAgeRangeQL;
}

type TimeSlotsQL = NonNullable<TicketRateQL['timeSlots']>;
type TimeSlotQL = TimeSlotsQL[number];
function toTimeSlot(timeSlotQL: TimeSlotQL): TimeSlot {
  assertSmartSearchValue(timeSlotQL);

  assertSmartSearchValue(timeSlotQL.startTime);
  const startTime = timeSlotQL.startTime;

  return {
    ...timeSlotQL,
    startTime,
  };
}
